import { useMemo, useEffect } from "react";
import "instantsearch.css/themes/satellite.css";
import "./SupplierInventorySearch.css";
import "./RefinementList.css";
import { useInfiniteHits } from "react-instantsearch";
import {
  Text,
  Box,
  Flex,
  Badge,
  Button,
  Icon,
  Tooltip,
  VStack,
  Spinner,
} from "@chakra-ui/react";
import { MdLocationOn } from "react-icons/md";
import { useState } from "react";
import { BsEyeFill } from "react-icons/bs";
import { FaUserCheck } from "react-icons/fa";
import { GetPinnedComponent } from "./PinnedSuppliers";
import SupplierDetailsModal from "./SupplierDetailsModal";
import RequestPartConfirmationModal from "./RequestPartConfirmationModal";
import { RequestPartConfirmationButton } from "./RequestPartConfirmationButton";
import {
  SupplierDiscoverySortOptions,
  EnrichedSupplierItem,
  OfflineSupplierEntry,
} from "./types";
import { fetchFulfilledCounts } from "./utils/fetchFulfilledCounts";
import {
  mergeOfflinePartsIntoHits,
  computeFulfilledCount,
} from "./utils/offlinePartAvailabilityUtils";
import {
  applySupplierInventoryDeltasToParts,
  getSupplierInventoryDeltaKey,
} from "./utils/supplierInventoryDeltaUtils";
import {
  applySupplierPartConfirmationToParts,
  getSupplierPartConfirmationKey,
} from "./utils/supplierPartConfirmationUtils";
import { PartConfirmationBucketEntry } from "./utils/partsCalculationUtils";
import { buildSupplierMetrics } from "./utils/supplierMetricsUtils";
import { SupplierProgressBadge } from "./SupplierBadge";
import { TopFulfilledSuppliers } from "./TopSuppliers";
import { AddToSupplierBucketOptions } from "./utils/assignQtyForSupplierParts";
import { Table, Thead, Tr, Th, Tbody, Td } from "@chakra-ui/react";
import { BADGE_CONFIGS } from "./config";
import { SupplierText } from "./SupplierBadge";
import { FaWarehouse } from "react-icons/fa";

function sortItemsByDistance(
  items: EnrichedSupplierItem[],
  sortOrder: "asc" | "desc",
): EnrichedSupplierItem[] {
  return [...items].sort((a, b) => {
    const result = a.distanceKm - b.distanceKm;
    return sortOrder === "asc" ? result : -result;
  });
}

function sortItemsByTotalPartsPercentage(
  items: EnrichedSupplierItem[],
  sortOrder: "asc" | "desc",
): EnrichedSupplierItem[] {
  return [...items].sort((a, b) => {
    const result = a.totalPartPercentage - b.totalPartPercentage;
    return sortOrder === "asc" ? -result : result;
  });
}

function sortItemsByFulfilledPercentage(
  items: EnrichedSupplierItem[],
  sortOrder: "asc" | "desc",
): EnrichedSupplierItem[] {
  return [...items].sort((a, b) => {
    const result = a.fulfilledPercent - b.fulfilledPercent;
    return sortOrder === "asc" ? -result : result;
  });
}

function applySortingOn(
  sortField: SupplierDiscoverySortOptions,
  finalItems: EnrichedSupplierItem[],
  sortOrder: "asc" | "desc",
) {
  switch (sortField) {
    case SupplierDiscoverySortOptions.DISTANCE:
      return sortItemsByDistance(finalItems, sortOrder);
    case SupplierDiscoverySortOptions.TOTALPARTSPERCENTAGE:
      return sortItemsByTotalPartsPercentage(finalItems, sortOrder);
    case SupplierDiscoverySortOptions.FULFILLEDPERCENTAGE:
      return sortItemsByFulfilledPercentage(finalItems, sortOrder);
    default:
      return finalItems;
  }
}

interface SupplierListProps {
  customerSupplierAssociations: any[];
  customerGeoPoint: { latitude: number; longitude: number };
  preFacetedPartNumbers: any[];
  handleSelectListItem: (item: any, supplierParts?: any[]) => void;
  handleAddToSelectedSupplierBucket: (
    item: any,
    supplierParts?: any[],
    options?: AddToSupplierBucketOptions,
  ) => void;
  onSelectionClose: () => void;
  typesenseInstantsearchAdapter: any;
  supplierDiscoveryCollectionName: string;
  geoDistanceFilter: string;
  supplier_discovery_parts_limit: number;
  selectedSuppliers?: any[];
  excludeSupplierIds?: string[];
  isFullyFulfilled?: boolean;
  isBucketSaveAllowed?: boolean;
  onSaveBucket?: () => void;
  setShowCreateAnotherBucket: (show: boolean) => void;
  supplierCityMap: Map<string, string>;
  searchInput: string;
  sortField: SupplierDiscoverySortOptions;
  isEstimateOrder: boolean;
  offlinePartAvailabilityDiscrepanciesPerSupplierMap?: Map<
    string,
    OfflineSupplierEntry
  >;
  supplierInventoryDeltaQuantityMap?: Map<string, number>;
  supplierPartConfirmationQuantityMap?: Map<string, number>;
  partConfirmationBucket?: PartConfirmationBucketEntry[];
  orderId: string;
  orderNo: string;
}

export const SupplierList: React.FC<SupplierListProps> = ({
  customerSupplierAssociations,
  customerGeoPoint,
  preFacetedPartNumbers,
  handleSelectListItem,
  handleAddToSelectedSupplierBucket,
  onSelectionClose,
  typesenseInstantsearchAdapter,
  supplierDiscoveryCollectionName,
  geoDistanceFilter,
  supplier_discovery_parts_limit,
  selectedSuppliers = [],
  excludeSupplierIds,
  isFullyFulfilled = false,
  isBucketSaveAllowed = false,
  onSaveBucket,
  setShowCreateAnotherBucket,
  supplierCityMap,
  searchInput,
  sortField,
  isEstimateOrder,
  offlinePartAvailabilityDiscrepanciesPerSupplierMap,
  supplierInventoryDeltaQuantityMap,
  supplierPartConfirmationQuantityMap,
  partConfirmationBucket,
  orderId,
  orderNo,
}) => {
  const [selectedSupplier, setSelectedSupplier] = useState("");
  const userLatitude = customerGeoPoint?.latitude;
  const userLongitude = customerGeoPoint?.longitude;
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const resolvedExcludeSupplierIds = useMemo(
    () =>
      excludeSupplierIds !== undefined
        ? excludeSupplierIds.map(String)
        : selectedSuppliers.map((supplier) => String(supplier.supplierId)),
    [excludeSupplierIds, selectedSuppliers],
  );
  const pinnedSupplierIds = useMemo(
    () => ["64ae87c051c0be36170dc718", ...(customerSupplierAssociations ?? [])],
    [customerSupplierAssociations],
  );
  const [isPinnedItem, setIsPinnedItem] = useState<number>(0);
  const [isTopSuppliers, setIsTopSuppliers] = useState<number>(0);
  const [pinnedHitMap, setPinnedHitMap] = useState<Map<string, any[]>>(
    new Map(),
  );
  const [topSupplierHitMap, setTopSupplierHitMap] = useState<
    Map<string, any[]>
  >(new Map());
  const [supplierHitMap, setSupplierHitMap] = useState<Map<string, any[]>>(
    new Map(),
  );
  const [supplierMetricsMap, setSupplierMetricsMap] = useState<
    Map<
      string,
      {
        distanceKm: number;
        coverage: number;
        isAssociated: boolean;
        supplierName: string;
        supplierId: string;
        totalHits: number;
        fulfilledCount?: number;
        fulfilledPercent: number;
        totalParts?: number;
        totalPartPercentage: number;
        supplierCity?: string;
      }
    >
  >(new Map());
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const { hits } = useInfiniteHits();

  const preFacetedPartNumbersMap: Map<string, number> = useMemo(() => {
    return new Map(
      preFacetedPartNumbers.map((part: any) => [
        part.rootPartNumber,
        part.quantity,
      ]),
    );
  }, [preFacetedPartNumbers]);

  const totalRequestedQuantity = useMemo(() => {
    return preFacetedPartNumbers.reduce(
      (sum: number, part: any) => sum + part.quantity,
      0,
    );
  }, [preFacetedPartNumbers]);

  useEffect(() => {
    let cancelled = false;
    const buildSupplierMetricsMap = async () => {
      const newSupplierHitMap = new Map<string, any[]>();
      const newSupplierMetricsMap = new Map<
        string,
        {
          distanceKm: number;
          coverage: number;
          isAssociated: boolean;
          supplierName: string;
          supplierId: string;
          totalHits: number;
          fulfilledCount: number;
          fulfilledPercent: number;
          totalParts: number;
          totalPartPercentage: number;
          supplierCity?: string;
        }
      >();

      const fulfilledMap = await fetchFulfilledCounts({
        adapter: typesenseInstantsearchAdapter,
        supplierDiscoveryCollectionName,
        preFacetedPartNumbers,
        geoDistanceFilter,
        excludeSupplierIds: resolvedExcludeSupplierIds,
        isPinned: false,
      });

      hits.forEach((hit) => {
        const groupKey = hit._group_key[0];
        if (!groupKey) return;
        const [, supplierId] = groupKey.split("::");

        let supplierParts = hit._grouped_hits;
        const offlineEntry =
          offlinePartAvailabilityDiscrepanciesPerSupplierMap?.get(groupKey);
        if (offlineEntry) {
          supplierParts = mergeOfflinePartsIntoHits(
            supplierParts,
            offlineEntry.parts,
            offlineEntry.updatedAt,
          );
        }
        supplierParts = applySupplierInventoryDeltasToParts({
          supplierId,
          parts: supplierParts,
          supplierInventoryDeltaQuantityMap,
        });
        supplierParts = applySupplierPartConfirmationToParts({
          supplierId,
          parts: supplierParts,
          supplierPartConfirmationQuantityMap,
          partConfirmationBucket,
        });
        newSupplierHitMap.set(groupKey, supplierParts);

        if (!newSupplierMetricsMap.has(groupKey)) {
          const [supplierName, supplierId, coords] = groupKey.split("::");
          if (pinnedSupplierIds.includes(supplierId)) {
            return;
          }

          if (topSupplierHitMap.has(groupKey)) return;

          const isAssociated =
            customerSupplierAssociations.includes(supplierId);

          const partsForMetrics = newSupplierHitMap.get(groupKey)!;
          const hasSupplierInventoryDeltaQuantity = partsForMetrics.some(
            (part) =>
              supplierInventoryDeltaQuantityMap?.has(
                getSupplierInventoryDeltaKey(supplierId, part.rootPartNumber),
              ),
          );
          const hasSupplierPartConfirmationQuantity = partsForMetrics.some(
            (part) =>
              supplierPartConfirmationQuantityMap?.has(
                getSupplierPartConfirmationKey(
                  supplierId,
                  part.rootPartNumber,
                ),
              ),
          );
          const syntheticHit = {
            found: partsForMetrics.length,
            hits: partsForMetrics,
          };
          const metrics = buildSupplierMetrics(
            syntheticHit,
            supplierName,
            supplierId,
            coords,
            userLatitude,
            userLongitude,
            customerSupplierAssociations,
            preFacetedPartNumbers,
            preFacetedPartNumbersMap,
            totalRequestedQuantity,
            {
              supplierCity: supplierCityMap.get(supplierId),
              fulfilledCountOverride:
                offlineEntry ||
                hasSupplierInventoryDeltaQuantity ||
                hasSupplierPartConfirmationQuantity
                  ? computeFulfilledCount(
                      partsForMetrics,
                      preFacetedPartNumbersMap,
                    )
                  : fulfilledMap.get(groupKey) || 0,
            },
          );
          if (metrics && (metrics.fulfilledCount > 0 || metrics.totalPartsQuantity > 0)) {
            newSupplierMetricsMap.set(groupKey, {
              ...metrics,
              coverage: metrics.coverage ?? Infinity,
              fulfilledPercent: metrics.fulfilledPercent ?? 0,
              totalParts: metrics.totalPartsQuantity,
            });
          }
        }
      });

      if (
        offlinePartAvailabilityDiscrepanciesPerSupplierMap &&
        offlinePartAvailabilityDiscrepanciesPerSupplierMap.size > 0
      ) {
        Array.from(
          offlinePartAvailabilityDiscrepanciesPerSupplierMap.entries(),
        ).forEach(([groupKey, entry]) => {
          if (pinnedSupplierIds.includes(entry.supplierId)) return;
          if (topSupplierHitMap.has(groupKey)) return;
          if (resolvedExcludeSupplierIds.includes(entry.supplierId)) return;
          if (newSupplierMetricsMap.has(groupKey)) return;

          let partsForMetrics: any[];
          if (newSupplierHitMap.has(groupKey)) {
            partsForMetrics = newSupplierHitMap.get(groupKey)!;
          } else {
            const updatedAt = entry.updatedAt
              ? Math.floor(new Date(entry.updatedAt).getTime())
              : undefined;
            const syntheticParts = entry.parts.map(
              (p: {
                rootPartNumber: string;
                familyQuantity: number;
                source?: string;
              }) => ({
                rootPartNumber: p.rootPartNumber,
                familyQuantity: p.familyQuantity,
                source: p.source,
                supplierInfo: entry.groupKey,
                ...(updatedAt != null && { updatedAt }),
              }),
            );
            partsForMetrics = applySupplierInventoryDeltasToParts({
              supplierId: entry.supplierId,
              parts: syntheticParts,
              supplierInventoryDeltaQuantityMap,
            });
            partsForMetrics = applySupplierPartConfirmationToParts({
              supplierId: entry.supplierId,
              parts: partsForMetrics,
              supplierPartConfirmationQuantityMap,
              partConfirmationBucket,
            });
            newSupplierHitMap.set(groupKey, partsForMetrics);
          }

          const [
            offlineSupplierName,
            offlineSupplierId,
            offlineSupplierCoords,
          ] = groupKey.split("::");
          const syntheticHit = {
            found: partsForMetrics.length,
            hits: partsForMetrics,
          };
          const metrics = buildSupplierMetrics(
            syntheticHit,
            offlineSupplierName,
            offlineSupplierId,
            offlineSupplierCoords,
            userLatitude,
            userLongitude,
            customerSupplierAssociations,
            preFacetedPartNumbers,
            preFacetedPartNumbersMap,
            totalRequestedQuantity,
            {
              distanceKmOverride: 0,
              supplierCity: supplierCityMap.get(entry.supplierId),
              fulfilledCountOverride: computeFulfilledCount(
                partsForMetrics,
                preFacetedPartNumbersMap,
              ),
            },
          );
          if (metrics && (metrics.fulfilledCount > 0 || metrics.totalPartsQuantity > 0)) {
            newSupplierMetricsMap.set(groupKey, {
              ...metrics,
              coverage: metrics.coverage ?? Infinity,
              fulfilledPercent: metrics.fulfilledPercent ?? 0,
              totalParts: metrics.totalPartsQuantity,
            });
          }
        });
      }

      pinnedHitMap.forEach((value, key) => {
        newSupplierHitMap.set(key, value);
      });

      topSupplierHitMap.forEach((value, key) => {
        newSupplierHitMap.set(key, value);
      });

      if (!cancelled) {
        setSupplierHitMap(newSupplierHitMap);
        setSupplierMetricsMap(newSupplierMetricsMap);
        setIsDataLoaded(true);
      }
    };
    buildSupplierMetricsMap();
    return () => {
      cancelled = true;
    };
  }, [
    hits,
    customerGeoPoint,
    customerSupplierAssociations,
    preFacetedPartNumbers,
    pinnedHitMap,
    pinnedSupplierIds,
    topSupplierHitMap,
    geoDistanceFilter,
    typesenseInstantsearchAdapter,
    supplierDiscoveryCollectionName,
    preFacetedPartNumbersMap,
    totalRequestedQuantity,
    selectedSuppliers,
    resolvedExcludeSupplierIds,
    offlinePartAvailabilityDiscrepanciesPerSupplierMap,
    supplierInventoryDeltaQuantityMap,
    supplierPartConfirmationQuantityMap,
    partConfirmationBucket,
  ]);

  const sortedSuppliers = useMemo(() => {
    const supplierEntries = Array.from(supplierMetricsMap.entries());

    const enrichedItems: EnrichedSupplierItem[] = supplierEntries.map(
      ([groupKey, metrics]) => ({
        label: groupKey,
        count: metrics.totalHits,
        supplierName: metrics.supplierName,
        supplierId: metrics.supplierId,
        distanceKm: metrics.distanceKm,
        coverage: metrics.coverage,
        isAssociated: metrics.isAssociated,
        isPartial: metrics.coverage !== 100,
        fulfilledCount: metrics.fulfilledCount,
        fulfilledPercent: metrics.fulfilledPercent,
        totalParts: metrics.totalParts,
        totalPartPercentage: metrics.totalPartPercentage,
        supplierCity: metrics.supplierCity,
      }),
    );

    const sortedItems = applySortingOn(sortField, enrichedItems, sortOrder);

    const formattedItems = sortedItems.map(
      (item) =>
        [
          item.label,
          {
            distanceKm: item.distanceKm,
            coverage: item.coverage,
            isAssociated: item.isAssociated,
            supplierName: item.supplierName,
            supplierId: item.supplierId,
            totalHits: item.count,
            fulfilledCount: item.fulfilledCount,
            fulfilledPercent: item.fulfilledPercent,
            totalParts: item.totalParts,
            totalPartPercentage: item.totalPartPercentage,
          },
        ] as [
          string,
          {
            distanceKm: number;
            coverage: number;
            isAssociated: boolean;
            supplierName: string;
            supplierId: string;
            totalHits: number;
            fulfilledCount: number;
            fulfilledPercent: number;
            totalParts: number;
            totalPartPercentage: number;
            supplierCity?: string;
          },
        ],
    );

    if (searchInput.trim() !== "") {
      const lowerSearch = searchInput.trim().toLowerCase();
      return formattedItems.filter(([_, metrics]) =>
        metrics.supplierName.toLowerCase().includes(lowerSearch),
      );
    }

    return formattedItems;
  }, [supplierMetricsMap, sortField, sortOrder, searchInput]);

  const finalItems = sortedSuppliers;

  const handleSupplierClick = (
    groupKey: string,
    isFromTopSuppliers: boolean = false,
  ) => {
    setSelectedSupplier(groupKey);
    setIsModalOpen(true);
    setIsFromTopSuppliers(isFromTopSuppliers);
  };

  const [isFromTopSuppliers, setIsFromTopSuppliers] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [confirmationSupplier, setConfirmationSupplier] = useState("");
  const [confirmationFromTop, setConfirmationFromTop] = useState(false);

  const handleRequestPartConfirmation = (
    groupKey: string,
    isFromTop: boolean = false,
  ) => {
    setConfirmationSupplier(groupKey);
    setConfirmationFromTop(isFromTop);
    setIsConfirmationModalOpen(true);
  };

  const confirmationHitMap = useMemo(() => {
    if (topSupplierHitMap.has(confirmationSupplier)) {
      return topSupplierHitMap;
    }
    if (pinnedHitMap.has(confirmationSupplier)) {
      return pinnedHitMap;
    }
    return supplierHitMap;
  }, [
    confirmationSupplier,
    topSupplierHitMap,
    pinnedHitMap,
    supplierHitMap,
  ]);

  useEffect(() => {
    const basicAllowed =
      typeof onSaveBucket === "function" && selectedSuppliers.length > 0;
    const noSuppliersToFulfill =
      finalItems.length === 0 &&
      isDataLoaded &&
      preFacetedPartNumbers.length > 0 &&
      !isFullyFulfilled &&
      isPinnedItem === 0 &&
      isTopSuppliers === 0;
    const shouldShow =
      basicAllowed && (isFullyFulfilled || noSuppliersToFulfill);

    setShowCreateAnotherBucket(Boolean(shouldShow));
  }, [
    isBucketSaveAllowed,
    onSaveBucket,
    selectedSuppliers.length,
    finalItems.length,
    isDataLoaded,
    preFacetedPartNumbers.length,
    isFullyFulfilled,
    isPinnedItem,
    isTopSuppliers,
  ]);

  return (
    <Box justifySelf="center" flex={3.5} w="100%">
      {!isDataLoaded && preFacetedPartNumbers.length > 0 && (
        <Box textAlign="center" mt={6}>
          <VStack spacing={4}>
            <Spinner size="lg" color="blue.500" thickness="3px" />
            <SupplierText textStyle="subtitle">
              Loading suppliers...
            </SupplierText>
          </VStack>
        </Box>
      )}

      {finalItems.length === 0 &&
        isPinnedItem === 0 &&
        isTopSuppliers === 0 &&
        !isFullyFulfilled &&
        preFacetedPartNumbers.length > 0 &&
        isDataLoaded && (
          <Box textAlign="center" mt={6}>
            <SupplierText textStyle="subtitle">
              The current remaining quantity cannot be fulfilled by available
              suppliers. Improve Virtual Inventory.
            </SupplierText>
            {isBucketSaveAllowed &&
              onSaveBucket &&
              selectedSuppliers.length > 0 && (
                <Button
                  mt={4}
                  colorScheme="blue"
                  variant="solid"
                  size="sm"
                  onClick={onSaveBucket}
                  bg={"#4b85f2"}
                >
                  Create Another Bucket
                </Button>
              )}
          </Box>
        )}
      <Box position="relative">
        <Box
          px={4}
          mx={-4}
          borderBottomRadius="xl"
          overflowY="auto"
          maxH="80vh"
          pb={1}
          overflowX="visible"
          sx={{
            "&::-webkit-scrollbar": { display: "none" },
            "-ms-overflow-style": "none",
            "scrollbar-width": "none",
          }}
        >
          <TopFulfilledSuppliers
            handleSupplierClick={handleSupplierClick}
            onRequestPartConfirmation={(groupKey) =>
              handleRequestPartConfirmation(groupKey, true)
            }
            pinnedSupplierIds={pinnedSupplierIds}
            setIsTopSuppliers={setIsTopSuppliers}
            preFacetedPartNumbers={preFacetedPartNumbers}
            typesenseInstantsearchAdapter={typesenseInstantsearchAdapter}
            onSelectionClose={onSelectionClose}
            customerSupplierAssociations={customerSupplierAssociations}
            customerGeoPoint={customerGeoPoint}
            handleSelectListItem={handleSelectListItem}
            setTopSupplierHitMap={setTopSupplierHitMap}
            supplierDiscoveryCollectionName={supplierDiscoveryCollectionName}
            preFacetedPartNumbersMap={preFacetedPartNumbersMap}
            totalRequestedQuantity={totalRequestedQuantity}
            topSupplierHitMap={topSupplierHitMap}
            supplier_discovery_parts_limit={supplier_discovery_parts_limit}
            handleAddToSelectedSupplierBucket={
              handleAddToSelectedSupplierBucket
            }
            selectedSuppliers={selectedSuppliers}
            excludeSupplierIds={resolvedExcludeSupplierIds}
            supplierCityMap={supplierCityMap}
            isEstimateOrder={isEstimateOrder}
            offlinePartAvailabilityDiscrepanciesPerSupplierMap={
              offlinePartAvailabilityDiscrepanciesPerSupplierMap
            }
            supplierInventoryDeltaQuantityMap={
              supplierInventoryDeltaQuantityMap
            }
            supplierPartConfirmationQuantityMap={
              supplierPartConfirmationQuantityMap
            }
            partConfirmationBucket={partConfirmationBucket}
          />

          <GetPinnedComponent
            handleSupplierClick={handleSupplierClick}
            onRequestPartConfirmation={handleRequestPartConfirmation}
            pinnedSupplierIds={pinnedSupplierIds}
            setIsPinnedItem={setIsPinnedItem}
            preFacetedPartNumbers={preFacetedPartNumbers}
            typesenseInstantsearchAdapter={typesenseInstantsearchAdapter}
            onSelectionClose={onSelectionClose}
            customerSupplierAssociations={customerSupplierAssociations}
            customerGeoPoint={customerGeoPoint}
            handleSelectListItem={handleSelectListItem}
            setPinnedHitMap={setPinnedHitMap}
            supplierDiscoveryCollectionName={supplierDiscoveryCollectionName}
            preFacetedPartNumbersMap={preFacetedPartNumbersMap}
            totalRequestedQuantity={totalRequestedQuantity}
            pinnedHitMap={pinnedHitMap}
            handleAddToSelectedSupplierBucket={
              handleAddToSelectedSupplierBucket
            }
            selectedSuppliers={selectedSuppliers}
            excludeSupplierIds={resolvedExcludeSupplierIds}
            supplierCityMap={supplierCityMap}
            isEstimateOrder={isEstimateOrder}
            offlinePartAvailabilityDiscrepanciesPerSupplierMap={
              offlinePartAvailabilityDiscrepanciesPerSupplierMap
            }
            supplierInventoryDeltaQuantityMap={
              supplierInventoryDeltaQuantityMap
            }
            supplierPartConfirmationQuantityMap={
              supplierPartConfirmationQuantityMap
            }
            partConfirmationBucket={partConfirmationBucket}
          />
          {isFullyFulfilled && selectedSuppliers.length > 0 ? (
            <Box
              marginTop={5}
              position="relative"
              overflow="hidden"
              p={8}
              textAlign="center"
            >
              <VStack spacing={4}>
                <Text fontSize="4xl">🎉</Text>
                <Text
                  fontSize="1.5rem"
                  fontWeight="bold"
                  color="green.600"
                  fontFamily="'Nunito','Inter', sans-serif"
                >
                  All Parts Fulfilled!
                </Text>
                <Text
                  fontSize="1rem"
                  color="gray.600"
                  fontFamily="'Nunito','Inter', sans-serif"
                >
                  Congratulations! All parts have been successfully fulfilled by
                  the selected suppliers.
                </Text>
                {isBucketSaveAllowed && onSaveBucket && (
                  <Button
                    mt={2}
                    colorScheme="blue"
                    variant="solid"
                    size="sm"
                    onClick={onSaveBucket}
                    bg={"#4b85f2"}
                  >
                    Create Another Bucket
                  </Button>
                )}
              </VStack>
            </Box>
          ) : finalItems.length > 0 ? (
            <Box
              borderRadius={"xl"}
              marginTop={5}
              position="relative"
              overflow="hidden"
              boxShadow="md"
            >
              <Box
                maxW="100%"
                px={{ base: 4, md: 8 }}
                py={{ base: 5, md: 6 }}
                borderRadius="xl"
                fontFamily="'Nunito','Inter', sans-serif"
                bg="#F3F4F6"
                paddingTop={4}
                paddingBottom={6}
                paddingLeft={6}
                paddingRight={6}
              >
                <Flex mb={4} alignItems="center" gap={2}>
                  <Text fontSize="lg" fontWeight="bold" color="gray.900">
                    <FaWarehouse color="#2966eb" size={20} />
                  </Text>
                  <SupplierText textStyle="header">
                    Additional Suppliers
                  </SupplierText>
                </Flex>

                <Box
                  borderRadius="xl"
                  overflowY="auto"
                  maxH="70vh"
                  boxShadow="md"
                  sx={{
                    "&::-webkit-scrollbar": { display: "none" },
                    "-ms-overflow-style": "none",
                    "scrollbar-width": "none",
                  }}
                >
                  <Table
                    fontFamily="'Nunito','Inter', sans-serif"
                    variant="simple"
                    size="m"
                    bg="white"
                    boxShadow="sm"
                    fontSize="0.85rem"
                    fontWeight={700}
                  >
                    <Thead
                      height={42}
                      bg="gray.50"
                      fontFamily="'Nunito','Inter', sans-serif"
                      position="sticky"
                      top={0}
                      zIndex={1}
                    >
                      <Tr>
                        <Th
                          textTransform="none"
                          color="gray.600"
                          width="40%"
                          fontFamily="'Nunito','Inter', sans-serif"
                          letterSpacing={0}
                        >
                          <Text
                            fontSize="0.95rem"
                            fontWeight="700"
                            color="gray.600"
                            marginLeft={4}
                          >
                            Suppliers Info
                          </Text>
                        </Th>
                        <Th
                          fontFamily="'Nunito','Inter', sans-serif"
                          textTransform="none"
                          textAlign="center"
                          color="gray.600"
                          width="15%"
                          letterSpacing={0}
                        >
                          <SupplierText
                            textStyle="label"
                            tooltip={BADGE_CONFIGS.fulfilled.tooltip}
                          >
                            {BADGE_CONFIGS.fulfilled
                              .label(0)
                              .replace("0% ", "")}
                          </SupplierText>
                        </Th>
                        <Th
                          textTransform="none"
                          textAlign="center"
                          color="gray.600"
                          width="15%"
                          fontFamily="'Nunito','Inter', sans-serif"
                          letterSpacing={0}
                        >
                          <SupplierText
                            textStyle="label"
                            tooltip={BADGE_CONFIGS.coverage.tooltip}
                          >
                            {BADGE_CONFIGS.coverage.label(0).replace("0% ", "")}
                          </SupplierText>
                        </Th>
                        <Th
                          textTransform="none"
                          textAlign="center"
                          color="gray.600"
                          width="15%"
                          fontFamily="'Nunito','Inter', sans-serif"
                          letterSpacing={0}
                        >
                          <SupplierText
                            textStyle="label"
                            tooltip={BADGE_CONFIGS.quantity.tooltip}
                          >
                            {BADGE_CONFIGS.quantity.label(0).replace("0% ", "")}
                          </SupplierText>
                        </Th>
                        <Th
                          textTransform="none"
                          textAlign="center"
                          color="gray.600"
                          width="15%"
                          fontFamily="'Nunito','Inter', sans-serif"
                          letterSpacing={0}
                        >
                          <SupplierText textStyle="label">Actions</SupplierText>
                        </Th>
                      </Tr>
                    </Thead>
                    <Tbody overflow={"auto"}>
                      {finalItems.map(([groupKey, metrics], index) => {
                        return (
                          <Tr
                            key={groupKey}
                            role="group"
                            cursor="pointer"
                            _hover={{ bg: "gray.50" }}
                            transition="all 0.2s"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddToSelectedSupplierBucket(
                                {
                                  _id: metrics.supplierId,
                                  supplierName: metrics.supplierName,
                                  distanceKm: metrics.distanceKm,
                                  supplierCity: metrics.supplierCity,
                                },
                                supplierHitMap.get(groupKey),
                              );
                            }}
                          >
                            <Td px={4} py={3} width="40%">
                              <Flex
                                align="center"
                                direction="column"
                                alignItems="flex-start"
                                width="100%"
                              >
                                <SupplierText textStyle="title">
                                  {metrics.supplierName}
                                </SupplierText>
                                <Flex
                                  mt={1}
                                  align="center"
                                  color="gray.500"
                                  gap={0.5}
                                  fontWeight={700}
                                  fontFamily="'Nunito','Inter', sans-serif"
                                  marginLeft={-0.5}
                                >
                                  <MdLocationOn
                                    size="0.95rem"
                                    color={
                                      metrics.distanceKm !== undefined &&
                                      metrics.distanceKm !== Infinity
                                        ? "#3b82f6"
                                        : "#E40133"
                                    }
                                  />
                                  <SupplierText textStyle="subtitle">
                                    {metrics.distanceKm !== undefined &&
                                    metrics.distanceKm !== Infinity
                                      ? `${metrics.distanceKm.toFixed(
                                          1,
                                        )} km away`
                                      : "Unavailable"}
                                  </SupplierText>
                                </Flex>
                                {metrics.isAssociated && (
                                  <Badge
                                    fontSize="xs"
                                    fontWeight="bold"
                                    borderRadius="full"
                                    px={2}
                                    colorScheme="blue"
                                    mt={1}
                                  >
                                    Associated
                                  </Badge>
                                )}
                              </Flex>
                            </Td>
                            <Td textAlign="center" px={4} py={3} width="15%">
                              <SupplierProgressBadge
                                type="fulfilled"
                                value={metrics.fulfilledPercent}
                              />
                            </Td>

                            {/* Coverage */}
                            <Td textAlign="center" px={4} py={3} width="15%">
                              <SupplierProgressBadge
                                type="coverage"
                                value={metrics.coverage}
                                totalHits={metrics.totalHits}
                              />
                            </Td>

                            {/* Quantity */}
                            <Td textAlign="center" px={4} py={3} width="15%">
                              {metrics.totalPartPercentage !== undefined ? (
                                <SupplierProgressBadge
                                  type="quantity"
                                  value={metrics.totalPartPercentage}
                                />
                              ) : (
                                "-"
                              )}
                            </Td>

                            {/* Actions */}
                            <Td textAlign="center" px={4} py={3} width="15%">
                              <Flex gap={2} justify="center">
                                
                                {!isEstimateOrder && (<Button
                                  size="sm"
                                  fontSize="xs"
                                  fontWeight="bold"
                                  leftIcon={
                                    <Icon
                                      as={FaUserCheck}
                                      boxSize={3.5}
                                      color="#10B981"
                                    />
                                  }
                                  px={3}
                                  py={1.5}
                                  borderRadius="md"
                                  bg="#f0fdf4"
                                  color="#16a34a"
                                  _hover={{ bg: "#dcfce7" }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSelectListItem(
                                      {
                                        _id: metrics.supplierId,
                                        supplierName: metrics.supplierName,
                                      },
                                      supplierHitMap.get(groupKey),
                                    );
                                    onSelectionClose();
                                  }}
                                >
                                  {isEstimateOrder
                                    ? "Generate Estimate"
                                    : "Allocate"}
                                </Button>)}
                                <RequestPartConfirmationButton
                                  size="sm"
                                  supplierId={metrics.supplierId}
                                  onClick={() =>
                                    handleRequestPartConfirmation(groupKey)
                                  }
                                />
                                <Button
                                  size="sm"
                                  fontSize="xs"
                                  fontWeight="bold"
                                  px={2}
                                  py={1.5}
                                  borderRadius="md"
                                  bg="#eff6ff"
                                  color="#4b85f2"
                                  _hover={{ bg: "#d2e4fb" }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSupplierClick(groupKey);
                                  }}
                                >
                                  <Icon
                                    as={BsEyeFill}
                                    boxSize={4}
                                    color="#2966eb"
                                  />
                                </Button>
                              </Flex>
                            </Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </Box>
              </Box>
            </Box>
          ) : null}
          {isModalOpen && (
            <SupplierDetailsModal
              handleSelectListItem={handleSelectListItem}
              handleAddToSelectedSupplierBucket={
                handleAddToSelectedSupplierBucket
              }
              supplierLabel={selectedSupplier}
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              preFacetedPartNumbers={preFacetedPartNumbers}
              typesenseInstantsearchAdapter={typesenseInstantsearchAdapter}
              finalHitMap={
                topSupplierHitMap.has(selectedSupplier)
                  ? topSupplierHitMap
                  : pinnedHitMap.has(selectedSupplier)
                    ? pinnedHitMap
                    : supplierHitMap
              }
              isFromTopSuppliers={isFromTopSuppliers}
              onSelectionClose={onSelectionClose}
              isEstimateOrder={isEstimateOrder}
              orderNo={orderNo}
              supplierPartConfirmationQuantityMap={
                supplierPartConfirmationQuantityMap
              }
              partConfirmationBucket={partConfirmationBucket}
            />
          )}
          {isConfirmationModalOpen && (
            <RequestPartConfirmationModal
              supplierLabel={confirmationSupplier}
              isOpen={isConfirmationModalOpen}
              onClose={() => setIsConfirmationModalOpen(false)}
              preFacetedPartNumbers={preFacetedPartNumbers}
              finalHitMap={confirmationHitMap}
              isFromTopSuppliers={confirmationFromTop}
              orderId={orderId}
              orderNo={orderNo}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};
